import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import theme from 'stories/utils/theme'
import { Headline4 } from 'stories/elements/Typography/Text'
import { ApiError } from 'services/ApiService'
import SearchService from 'services/SearchService'
import { SearchContent } from 'interfaces/Search'
import _useTranslation from 'lib/hooks/_useTranslation'
import Search from 'components/elements/Search'
import KnowDisplayCard from 'components/modules/Knows/KnowDisplayCard'
import Spinner from 'components/elements/Spinner'
import InfiniteScroll from 'react-infinite-scroller'

const Container = styled.div`
    margin-top: 0px;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 30px;
    place-content: start center;
    position: relative;

    h4 {
        padding: 25px 10%;
    }
`

const Row = styled.div`
    .list {
        display: flex;
        gap: 10px;
        justify-content: center;
        flex-wrap: wrap;
    }
    min-height: 350px;
    max-height: 600px;
    overflow: auto;
`

interface Props {
    open: boolean
    handleClose: () => void
    onKnowChoosen: (knowUuid: string) => void
    knowDisplayCountLimit?: number
}

interface InifiniteScrollProps extends InfiniteScroll {
    pageLoaded: number
}

const KnowSelectionModal = ({ open, handleClose, onKnowChoosen, knowDisplayCountLimit = 12 }: Props) => {
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [searchContents, setSearchContents] = useState<SearchContent[]>([])
    const [hasMorePages, setMorePages] = useState(false)
    const { t: tCommon } = _useTranslation('common')

    const scrollRef = useRef<InifiniteScrollProps>(null)

    const handleSearch = (query: string) => {
        setSearchQuery(query)
        if (scrollRef.current) scrollRef.current.pageLoaded = 0
    }

    const loadKnows = useCallback(
        (pageNumber: number) => {
            SearchService.getKnows(searchQuery, knowDisplayCountLimit, pageNumber)
                .then((response) => {
                    if (pageNumber) {
                        setSearchContents((knows) => [...knows, ...response.knows])
                    } else {
                        setSearchContents(response.knows)
                    }
                    setMorePages(response.hasMore)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'search.getKnows')
                    } else {
                        throw error
                    }
                })
        },
        [knowDisplayCountLimit, searchQuery, tCommon]
    )

    useEffect(() => {
        loadKnows(0)
    }, [loadKnows])

    return (
        <FeatureDialog
            open={open}
            onClose={handleClose}
            ariaPrefix="choose-message-know"
            title={tCommon('common/sendKnow')}
            backgroundColor={theme.colors.paperGrey}
        >
            <Container>
                <Search
                    onSearch={handleSearch}
                    defaultValue={searchQuery}
                    placeholder={tCommon('common/searchByTitleLabel')}
                    buttonText={tCommon('common/searchButtonLabel')}
                />
                <Row>
                    {searchContents?.length ? (
                        <InfiniteScroll
                            pageStart={0}
                            loadMore={loadKnows}
                            hasMore={hasMorePages}
                            loader={<Spinner forceShow key={0} />}
                            useWindow={false}
                            threshold={5}
                            ref={scrollRef}
                        >
                            <div className="list">
                                {searchContents &&
                                    searchContents.map((content) =>
                                        content.know ? (
                                            <KnowDisplayCard
                                                key={content.know.uuid}
                                                knowUuid={content.know.uuid}
                                                referrerScreen="chatChooseKnowDialog"
                                                know={null}
                                                onKnowClick={onKnowChoosen}
                                            />
                                        ) : null
                                    )}
                            </div>
                        </InfiniteScroll>
                    ) : (
                        <Headline4 color={theme.colors.headlineDark} textAlign="center">
                            {tCommon('common/noKnowsFound')}
                        </Headline4>
                    )}
                </Row>
            </Container>
        </FeatureDialog>
    )
}

export default KnowSelectionModal
